import { updateMyProfile, type FanProfile } from '../api/fan-api';
import { FanProfileForm } from './FanProfileForm';

/**
 * Bottom-sheet edit flow for the Profile page. Reuses the same
 * FanProfileForm body that the first-login setup modal uses, but
 * mounts as a non-blocking sheet with a cancel button.
 *
 * Backend behaviour: PUT /api/fan/me preserves profileCompletedAt
 * on subsequent edits (it's a "set on first completion" marker,
 * not a last-edited timestamp), and a missing birthday/gender on
 * the PUT body REMOVES the column — so a fan can clear an
 * optional field by submitting an empty value here.
 */
interface Props {
  open: boolean;
  current: FanProfile;
  onClose: () => void;
  /** Called after a successful save with the patched profile, so
   *  the parent (Profile page) can refresh its display without an
   *  extra GET. */
  onSaved: (next: FanProfile) => void;
}

export function EditProfileSheet({ open, current, onClose, onSaved }: Props) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-profile-sheet-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl p-5 sm:p-6 space-y-5 max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: `calc(1.25rem + env(safe-area-inset-bottom))` }}
      >
        {/* Drag-handle decoration on mobile */}
        <div className="sm:hidden absolute top-2 inset-x-0 flex justify-center pointer-events-none">
          <div className="h-1 w-10 rounded-full bg-slate-700" />
        </div>

        <header className="mt-2 sm:mt-0">
          <h2 id="edit-profile-sheet-title" className="text-lg font-semibold text-slate-100">
            編輯個人資料
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            可以更新暱稱、生日、性別。Email 與加入時間無法更動。
          </p>
        </header>

        <FanProfileForm
          initial={{
            displayName: current.displayName || '',
            ...(current.birthday ? { birthday: current.birthday } : {}),
            ...(current.gender ? { gender: current.gender } : {}),
          }}
          submitLabel="儲存"
          onCancel={onClose}
          onSubmit={async (values) => {
            const next = await updateMyProfile(values);
            onSaved(next);
            onClose();
          }}
        />
      </div>
    </div>
  );
}
